# THE PARENT / STUDENT SHELL'S ROUTES (/app) — card 6ac022caa664bc34d57be8c1.
#
# Keyed by URL, the shape a schema App row's manifest has; router/hash_router
# says what an entry may carry. The keys are the URLs these screens have always
# had: they are mailed, bookmarked and linked, and tests/pins/legacyHashUrls
# holds the list that must keep opening the same screen with the same params.
import { screen } from './shell.coffee'
import {ENROLL_COMPLETE_STATE, ENROLL_COMPLETE_PATH} from './StudentApplication/enrollRedirect.coffee'
import {DELETE_ACCOUNT_STATE} from './subscriptionRouting.coffee'
import {landingRoute} from './landing.coffee'

AffiliateSetup     = screen (-> import('./Affiliate/setup.coffee')), 'setup'
Assignment         = screen (-> import('./Assignment/assignment.coffee')), 'assignment'
Calendar           = screen (-> import('./Calendar/calendar.coffee')), 'calendar'
Help               = screen (-> import('./Help/help.coffee')), 'help'
SubscriptionExpired = screen (-> import('./SubscriptionExpired/subscription_expired.coffee')), 'subscription_expired'
Login              = screen (-> import('./Login/login.coffee')), 'login'
Dashboard          = screen (-> import('./Dashboard/dashboard.coffee')), 'dashboard'
ManageAccount      = screen (-> import('./ManageAccount/account.coffee')), 'account'
MilestoneReport    = screen (-> import('./MilestoneReport/milestone_report.coffee')), 'milestone_report'
LongMilestoneReport    = screen (-> import('./MilestoneReport/long_milestone_report.coffee')), 'long_milestone_report'
GameLayout         = screen (-> import('./Game/game_layout.coffee')), 'game_layout'
StudentReport      = screen (-> import('./StudentReport/student_report.coffee')), 'student_report'
StudentApplication = screen (-> import('./StudentApplication/application.coffee')), 'application'
EnrollComplete     = screen (-> import('./StudentApplication/enroll_complete.coffee')), 'enroll_complete'
UpdateScholarship  = screen (-> import('./UpdateScholarship/update_scholarship.coffee')), 'update_scholarship'
GamesList          = screen (-> import('./GameList/games.coffee')), 'games'
SubjectList       = screen (-> import('./SubjectList/subjects.coffee')), 'subjects'
TeacherUpgrade     = screen (-> import('./TeacherUpgrade/teacher_upgrade.coffee')), 'teacher_upgrade'
Tokens             = screen (-> import('./Tokens/tokens.coffee')), 'tokens'
HistoryPlayer      = screen (-> import('./GamePlayback/player.coffee')), 'player'
CancellationSurvey = screen (-> import('./CancellationSurvey/cancellation_survey.coffee')), 'cancellation_survey'
Cancel             = screen (-> import('./CancellationSurvey/new_cancellation_survey.coffee')), 'new_cancellation_survey'
Worksheet          = screen (-> import('./Dashboard/worksheet.coffee')), 'worksheet'
DeleteAccount      = screen (-> import('./DeleteAccount/delete_account.coffee')), 'delete_account'
CoachingVideos     = screen (-> import('./CoachingVideos/videos.coffee')), 'videos'

# The guards a route turns on with a flag, run in this order.
#
# requireLogin keeps where the visitor was going, so signing in takes them
# there (login.coffee reads original_target / original_params).
requireLogin = (target) ->
  unless window.app?.loopbackAPI?.isLoggedIn()
    original_target = target.name
    original_params = target.params
    console.log "Redirecting logged-out user to the login page. Original target: #{original_target}, Original Params:"
    console.dir original_params
    return { state: 'login', params: { original_target, original_params } }

# Manage Account and QR Logins are the parent's screens. The drawer hides them
# from a student-locked (QR) session (title_bar.coffee), but a typed or
# bookmarked #/account still rendered the whole page, so the route refuses
# them too and sends the student to their dashboard (card
# 6aa4d3ed1c031a8daf2e49c3).
requireParent = (target) ->
  loginRedirect = requireLogin(target)
  return loginRedirect if loginRedirect?
  if window.app?.isStudentLock?()
    console.log "Student-locked session: #{target.name} is a parent screen, redirecting to the dashboard"
    return { state: landingRoute() }

export guards = {requireLogin, requireParent}

# The refresh games / subjects make on the way in, keeping the active child's
# session clock.
#
# `student?` — the active student is not guaranteed here. This resolve runs
# whenever needsRefresh() says yes, and since card 6aade038bf4219e77902d92f
# that includes an Application that has never loaded an account, where there
# is no active student to preserve a session_duration from. The refresh below
# is exactly what fills it in.
refreshKeepingSession = ->
  if window.app.needsRefresh()
    student = window.app.getActiveStudent()
    session_duration = if student?.session_duration? then student.session_duration else null
    p = window.app.refresh().then ->
      window.app.getActiveStudent()?.session_duration = session_duration if session_duration?
    return p

refreshIfNeeded = ->
  if window.app.needsRefresh() then return window.app.refresh() else return null

# The play screens read the child's placement before they mount.
playResolve =
  studentRefresh: ({studentId}) ->
    console.log "Refreshing student #{studentId}..."
    response = await window.app.loopbackAPI.PosetProfile.find({where: {studentId: studentId}, include: {relation: 'poset', scope: {fields: ['name']}}})
    student = window.app.getStudentById(studentId)
    if student
      student.posetProfiles = response.data
      window.app.dirty = true
  games: ({studentId}) ->
    window.app.trackPageView("2: Game Play")
    # studentId comes from the route's params, NOT from the active student:
    # this resolve runs BEFORE digestParams latches it, so reading the active
    # student here filters the game voices for the child being navigated away
    # from (card 6aa4313530706c48305d2195).
    window.app.loadGames(studentId)

export routes =
  '':
    name: 'login'
    component: Login
    params:
      original_target: {value: null}
      original_params: {value: null}
    redirectTo: ->
      if window.app?.loopbackAPI?.isLoggedIn()
        # NEVER SEND A SESSION TO A SCREEN ITS OWN MANIFEST CANNOT RENDER —
        # card 6aaea3e58887cd49ef88bfc3.
        #
        # This arm used to be `else`: anything whose status was not exactly
        # 'Trial' went to the dashboard. A parent part-way through enrollment has
        # no 'Trial' status, no type and no students, and their client manifest
        # therefore carries no Student.active_history — which Dashboard/
        # overview.coffee and history.coffee both call on mount. Typing
        # /app#/login gave them 'We have encountered an error' and two CrashLog
        # rows (production fingerprints 5ddbcc8c and 5d257364, 2026-09-19).
        #
        # The boot chain in initialize() already gets this case right: a login
        # with no students goes to '#/application'. The bug was that this router
        # state kept a SECOND, weaker test of the same question — status ==
        # 'Trial' — and it is the only one consulted when the URL is #/login. So
        # ask the manifest itself, which is the thing that actually decides
        # whether the dashboard can render: exactly the reading card
        # 6aa6cd1fcbca3e2eac7b42ef settled for the student-lock case one session
        # state over.
        #
        # Fail CLOSED on purpose: when the method is missing we send them to the
        # enrollment form, which every session can render, rather than guessing.
        account = window.app.login?.account
        dashboardIsServed = typeof window.app.loopbackAPI?.Student?.active_history == 'function'
        if account?.status == 'Trial' or not dashboardIsServed
          console.log "Redirecting logged-in user to the application (status #{account?.status}, dashboard served: #{dashboardIsServed})"
          return { state: 'application' }
        else
          console.log "Redirecting logged-in user to the dashboard..."
          return { state: landingRoute() }
    resolve:
      none: -> window.app.trackPageView("2: Login")

  # #/logout reached WITHOUT a page load — a hash link, a typed hash, a goto
  # from inside the app — was never a state: the URL fell through to
  # `otherwise` and a Trial account was routed back to #/application still
  # signed in (card 6aa9ab4ba13a32e41e79f8a6). Signing out here makes it the
  # same logout the page-load branch performs.
  '/logout':
    name: 'logout'
    redirectTo: ->
      window.app.loopbackAPI.logout() if window.app?.loopbackAPI?.isLoggedIn()
      { state: 'login' }

  '/affiliate_setup':
    name: 'affiliate_setup'
    requireLogin: true
    component: AffiliateSetup
    resolve:
      none: -> window.app.trackPageView("2: Affiliate Setup")

  '/application':
    name: 'application'
    requireLogin: true
    component: StudentApplication
    resolve:
      none: ->
        window.app.trackPageView("2: Application")
        window.app.trackLead()
        refreshIfNeeded()

  # Where Stripe sends the browser back after a redirect-based payment method
  # during sign-up (card 6a9cb1d0ea6ab4f8aea9c463). Stripe's parameters ride
  # on the QUERY string, not on this hash route, so the url carries none.
  "#{ENROLL_COMPLETE_PATH}":
    name: ENROLL_COMPLETE_STATE
    requireLogin: true
    component: EnrollComplete
    resolve:
      none: ->
        window.app.trackPageView("2: Enrollment Complete")
        refreshIfNeeded()

  '/account?plan&tab':
    name: 'account'
    requireParent: true
    component: ManageAccount
    resolve:
      none: ->
        window.app.trackPageView("2: Account")
        refreshIfNeeded()

  # The deletion page (card 6abdc5fa7009a562af26012d). Reached from Manage
  # Account, and from the iOS app in Safari — where a signed-out visitor signs
  # in and comes back here (requireLogin keeps the original target).
  '/delete_account':
    name: DELETE_ACCOUNT_STATE
    requireParent: true
    component: DeleteAccount
    resolve:
      none: ->
        window.app.trackPageView("2: Delete Account")
        refreshIfNeeded()

  '/videos':
    name: 'videos'
    requireLogin: true
    component: CoachingVideos
    resolve:
      videos: ->
        window.app.trackPageView("2: Coaching Videos")
        window.app.loopbackAPI.Video.find({order: "position ASC"})

  '/assignment':
    name: 'assignment'
    requireLogin: true
    component: Assignment
    resolve:
      assignments: ->
        window.app.trackPageView("2: Assignment")
        window.app.loopbackAPI.Assignment.find({})

  '/calendar':
    name: 'calendar'
    requireLogin: true
    component: Calendar
    resolve:
      none: -> window.app.trackPageView("2: Calendar")

  '/dashboard?studentId':
    name: 'dashboard'
    # dynamic: the dashboard writes the selected child here as the parent swipes
    # (card 6aa8b5d750645cd1c3e765ab) without remounting itself.
    dynamic: ['studentId']
    requireLogin: true
    component: Dashboard
    resolve:
      initialized: ->
        window.app.trackPageView("2: Dashboard")
        refreshIfNeeded()

  '/games?studentId':
    name: 'games'
    requireLogin: true
    component: GamesList
    resolve:
      games: ({studentId}) ->
        window.app.trackPageView("2: Game List")
        # studentId comes from the route's params, NOT from the active student:
        # this resolve runs BEFORE digestParams latches it, so reading the
        # active student here filters the game voices for the child being
        # navigated away from (card 6aa4313530706c48305d2195).
        window.app.loadGames(studentId)
      refresh: refreshKeepingSession

  '/subjects?studentId':
    name: 'subjects'
    requireLogin: true
    component: SubjectList
    resolve:
      subjects: -> window.app.trackPageView("2: Subject List")
      refresh: refreshKeepingSession

  '/play/:gameId/:studentId?nodeId&activityId&successAnimationId&closeOnComplete':
    name: 'play'
    requireLogin: true
    component: GameLayout
    resolve: playResolve

  '/playsubject/:subjectId/:studentId?nodeId&activityId&successAnimationId&closeOnComplete':
    name: 'playSubject'
    requireLogin: true
    component: GameLayout
    resolve: playResolve

  '/playback/:history_id':
    name: 'playback'
    requireLogin: true
    component: HistoryPlayer
    resolve:
      history_item: ({history_id}) ->
        window.app.trackPageView("2: Playback")
        lbapi = window.app.loopbackAPI
        return lbapi.History.findById({id: history_id}, {include: [{game: 'gameTheme'}, 'student', 'poset', 'milestone', 'node']})
      recording: ({history_id}) ->
        return window.app.fetchHistory(history_id)

  '/student_report?studentId':
    name: 'student_report'
    requireLogin: true
    component: StudentReport
    resolve:
      initialized: ->
        window.app.trackPageView("2: Student Report")
        refreshIfNeeded()

  '/milestone_report?studentId':
    name: 'milestone_report'
    requireLogin: true
    component: MilestoneReport
    resolve:
      initialized: ->
        window.app.trackPageView("2: Placement Exam Report")
        refreshIfNeeded()

  '/long_milestone_report?studentId':
    name: 'long_milestone_report'
    requireLogin: true
    component: LongMilestoneReport
    resolve:
      initialized: ->
        window.app.trackPageView("2: Long Placement Exam Report")
        refreshIfNeeded()

  '/teacher_upgrade':
    name: 'teacher_upgrade'
    requireLogin: true
    component: TeacherUpgrade
    resolve:
      initialized: -> window.app.trackPageView("2: Teacher Upgrade")

  '/tokens':
    name: 'tokens'
    requireParent: true
    component: Tokens
    resolve:
      initialized: ->
        window.app.trackPageView("2: Login Tokens")
        # REFRESH ON THE WAY IN, UNCONDITIONALLY — card 6ab167dba4d698cda2b1fa9c.
        # This was the one route whose resolve did not refresh at all, so the QR
        # sheet rendered whatever window.app.students happened to hold: a tab
        # open since before a child was archived (on another device, or in the
        # other shell) kept offering that child a working login.
        #
        # `if window.app.needsRefresh()` — what every other route asks — is NOT
        # enough here, and measuring it is the only way to see that: needsRefresh
        # answers true only for a tab that made the change itself (window.app.dirty)
        # or one older than thirty minutes. A child archived on ANOTHER device
        # sets neither, so the guarded form left this screen listing the archived
        # child exactly as before (measured on ripperws2 2026-09-21).
        #
        # So this screen always re-reads. It is entered deliberately and rarely,
        # it costs one getAccount, and what it renders is PRINTED and handed to a
        # classroom — a stale list here is a child signing in after the teacher
        # believes they cannot.
        return window.app.refresh()

  '/update_scholarship?back':
    name: 'update_scholarship'
    requireLogin: true
    component: UpdateScholarship
    resolve:
      initialized: -> window.app.trackPageView("2: Update Scholarship")

  '/cancellation_survey':
    name: 'cancellation_survey'
    requireLogin: true
    component: CancellationSurvey
    resolve:
      initialized: -> window.app.trackPageView("2: Cancellation Survey")

  '/cancel':
    name: 'cancel'
    requireLogin: true
    component: Cancel
    resolve:
      initialized: -> window.app.trackPageView("2: Cancel")

  '/help':
    name: 'help'
    params:
      url: {value: null}
    requireLogin: true
    component: Help
    resolve:
      initialized: -> window.app.trackPageView("2: Help")

  # A student-locked session whose family's subscription has ended (card
  # 6aa6cd1fcbca3e2eac7b42ef). Reads nothing, so it renders on the
  # Student.unsubscribed manifest the dashboard cannot.
  '/subscription_expired':
    name: 'subscription_expired'
    requireLogin: true
    component: SubscriptionExpired
    resolve:
      initialized: -> window.app.trackPageView("2: Subscription Expired")

  '/worksheet?studentId':
    name: 'worksheet'
    requireLogin: true
    component: Worksheet
    resolve:
      initialized: ->
        window.app.trackPageView("2: Worksheet")
        refreshIfNeeded()
